import { Injectable, NgZone } from '@angular/core';
import {
	Auth,
	authState,
	ConfirmationResult,
	createUserWithEmailAndPassword,
	GoogleAuthProvider,
	PhoneAuthProvider,
	RecaptchaVerifier,
	sendEmailVerification,
	sendPasswordResetEmail,
	signInWithCredential,
	signInWithEmailAndPassword,
	signInWithPhoneNumber,
	signInWithPopup,
	User,
	UserCredential,
} from '@angular/fire/auth';
import { collection, CollectionReference, collectionSnapshots, DocumentData, Firestore, query, where } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, map, Observable, Subscription } from 'rxjs';
import { isStaleEmulatorAuthError, shouldUseFirebaseEmulators } from '../../../../firebase-emulator-auth';
import { useFirebaseEmulators } from 'src/environments/environment';
import { gapiSettings } from 'src/environments/gapisettings';
import { Account, gapi } from '../models';
import { AdvancedDebugging, isExpectedFirebaseAuthUserError } from '../utilities';

@Injectable({
	providedIn: 'root',
})
export class GoogleIdentityService {
	private static readonly opUsePhoneCredentials = 'app:GoogleIdentityService.usePhoneCredentials';
	private static readonly opSignUpWithEmail = 'app:GoogleIdentityService.signUpWithEmail';
	private static readonly opForgotPassword = 'app:GoogleIdentityService.forgotPassword';
	private static readonly opInitToken = 'app:GoogleIdentityService.initToken';
	private static readonly opAuthState = 'app:GoogleIdentityService.authState';
	private static readonly opSignInWithEmail = 'app:GoogleIdentityService.signInWithEmail';
	private static readonly opSendVerificationMail = 'app:GoogleIdentityService.sendVerificationMail';
	private static readonly opSignInPopup = 'app:GoogleIdentityService.signInPopup';
	private static readonly opSignInWithPhone = 'app:GoogleIdentityService.signInWithPhone';
	private static readonly opNavigateVerifyEmail = 'app:GoogleIdentityService.navigate.verifyEmail';

	private readonly advancedDebugging = new AdvancedDebugging();
	private user$: Observable<User | null>;
	private showDock = true;
	private hasAuthUser: string | null = null;
	private idToken: string | null = null;
	private accountGAPI = new BehaviorSubject<gapi | undefined>(undefined);
	private gapiReadySubject = new BehaviorSubject(false);
	private gapiTokenSub?: Subscription;
	/** Prevents overlapping Google popup sign-in calls (Firebase auth/cancelled-popup-request). */
	private signInPopupPromise: Promise<UserCredential> | null = null;
	public token$: Observable<gapi | undefined> = this.accountGAPI.asObservable();
	/** True after the first Firestore snapshot for {@link initToken} (avoids OAuth while gapi is still loading). */
	public readonly gapiReady$: Observable<boolean> = this.gapiReadySubject.asObservable();

	constructor(public auth: Auth, private router: Router, public ngZone: NgZone, public firestore: Firestore) {
		this.user$ = authState(this.auth as any);

		this.user$.subscribe({
			next: async user => {
				if (user?.email) this.hasAuthUser = user.email;
				const id = await user?.getIdToken();
				if (id) this.idToken = id;
			},
			error: error =>
				this.advancedDebugging.captureError(GoogleIdentityService.opAuthState, error, {
					tags: { operation: GoogleIdentityService.opAuthState },
				}),
		});
	}

	get isSignedIn(): string | null {
		return this.hasAuthUser;
	}

	get getUser() {
		return this.user$;
	}

	get getUserIdToken() {
		return this.idToken;
	}

	/** Firebase ID token for HTTP calls to gapi (optionally force-refresh on local emulators). */
	async getIdTokenForHttp(forceRefresh = false): Promise<string | null> {
		const user = this.auth.currentUser ?? (await firstValueFrom(this.getUser));
		if (!user) {
			return null;
		}
		try {
			return await user.getIdToken(forceRefresh);
		} catch (error: unknown) {
			const code = (error as { code?: string })?.code;
			if (isStaleEmulatorAuthError(code) && shouldUseFirebaseEmulators(useFirebaseEmulators)) {
				await this.signOut();
				return null;
			}
			if (code !== 'auth/network-request-failed') {
				this.advancedDebugging.captureError('app:GoogleIdentityService.getIdTokenForHttp', error, {
					tags: { operation: 'app:GoogleIdentityService.getIdTokenForHttp' },
				});
			}
			return null;
		}
	}

	set setDock(state: boolean) {
		this.showDock = state;
	}

	get getDock() {
		return this.showDock;
	}

	initToken(email: string) {
		this.gapiTokenSub?.unsubscribe();
		this.gapiReadySubject.next(false);

		this.gapiTokenSub = collectionSnapshots<Account>(
			query<Account, DocumentData>(
				collection(this.firestore, 'mas-accounts') as CollectionReference<Account>,
				where('emailAddresses.value', '==', email),
			),
		)
			.pipe(
				map(changes =>
					changes.map(a => {
						const data = a.data();
						if (data.mas?.gapi?.masAccountId) data.mas.gapi!.masAccountId = a.id;
						return data.mas?.gapi;
					}),
				),
			)
			.subscribe({
				next: gapis => {
					const withToken = gapis.filter((g): g is gapi => !!g?.token?.refresh_token);
					this.accountGAPI.next(withToken[0]);
					this.gapiReadySubject.next(true);
				},
				error: error => {
					this.advancedDebugging.captureError(GoogleIdentityService.opInitToken, error, {
						tags: { operation: GoogleIdentityService.opInitToken },
					});
					this.gapiReadySubject.next(true);
				},
			});
	}

	/*
		Sign in with email/password
	*/
	async signInWithEmail(email: string, password: string) {
		try {
			return await signInWithEmailAndPassword(this.auth, email, password);
		} catch (error) {
			if (!isExpectedFirebaseAuthUserError(error)) {
				this.advancedDebugging.captureError(GoogleIdentityService.opSignInWithEmail, error, {
					tags: { operation: GoogleIdentityService.opSignInWithEmail },
				});
			}
			throw error;
		}
	}

	/*
		Sign up with email/password
	*/
	async signUpWithEmail(email: string, password: string) {
		return await createUserWithEmailAndPassword(this.auth, email, password)
			.then(result => {
				this.sendVerificationMail();
				return GoogleAuthProvider.credentialFromResult(result);
			})
			.catch(error => {
				this.advancedDebugging.captureError(GoogleIdentityService.opSignUpWithEmail, error, {
					tags: { operation: GoogleIdentityService.opSignUpWithEmail },
				});
				throw error;
			});
	}

	/*
		Send email verfificaiton when new user sign up
	*/
	sendVerificationMail() {
		if (!this.auth.currentUser) {
			return;
		}
		sendEmailVerification(this.auth.currentUser)
			.then(() =>
				void this.router.navigate(['verify-email-address']).catch(error =>
					this.advancedDebugging.captureError(GoogleIdentityService.opNavigateVerifyEmail, error, {
						tags: { operation: GoogleIdentityService.opNavigateVerifyEmail },
					}),
				),
			)
			.catch(error =>
				this.advancedDebugging.captureError(GoogleIdentityService.opSendVerificationMail, error, {
					tags: { operation: GoogleIdentityService.opSendVerificationMail },
				}),
			);
	}

	/*
		Reset Forgot password
	*/
	async forgotPassword(passwordResetEmail: string) {
		return await sendPasswordResetEmail(this.auth, passwordResetEmail)
			.then(() => {
				window.alert('Password reset email sent, check your inbox.');
			})
			.catch(error => {
				this.advancedDebugging.captureError(GoogleIdentityService.opForgotPassword, error, {
					tags: { operation: GoogleIdentityService.opForgotPassword },
				});
				window.alert(error);
			});
	}

	/*
		Auth logic to run auth providers
	*/
	async signInPopup() {
		if (this.signInPopupPromise) {
			return this.signInPopupPromise;
		}

		const provider = new GoogleAuthProvider();
		provider.addScope(gapiSettings.Cfg.scope);

		// Injected Auth (emulator-connected in app.module provideAuth). Bare getAuth() breaks popup relay.
		this.signInPopupPromise = signInWithPopup(this.auth, provider).finally(() => {
			this.signInPopupPromise = null;
		});

		try {
			return await this.signInPopupPromise;
		} catch (error) {
			if (!isExpectedFirebaseAuthUserError(error)) {
				this.advancedDebugging.captureError(GoogleIdentityService.opSignInPopup, error, {
					tags: { operation: GoogleIdentityService.opSignInPopup },
				});
			}
			throw error;
		}
	}

	async signOut() {
		this.gapiTokenSub?.unsubscribe();
		this.gapiTokenSub = undefined;
		this.gapiReadySubject.next(false);
		this.accountGAPI.next(undefined);
		return await this.auth.signOut();
	}

	getRecaptcha() {
		return new RecaptchaVerifier(this.auth, 'recaptcha-container', {
			size: 'invisible',
			callback: (response: any) => response,
			'expired-callback': () => 'expired',
		});
	}

	async signInWithPhone(appVerifier: any, phoneNumber: string) {
		try {
			return await signInWithPhoneNumber(this.auth, phoneNumber, appVerifier);
		} catch (error) {
			this.advancedDebugging.captureError(GoogleIdentityService.opSignInWithPhone, error, {
				tags: { operation: GoogleIdentityService.opSignInWithPhone },
			});
			throw error;
		}
	}

	async usePhoneCredentials(confirmationResult: ConfirmationResult, verificationCode: string) {
		const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, verificationCode);

		return await signInWithCredential(this.auth, credential)
			.catch(error => {
				this.advancedDebugging.captureError(GoogleIdentityService.opUsePhoneCredentials, error, {
					tags: { operation: GoogleIdentityService.opUsePhoneCredentials },
				});
				window.alert(error.message);
			});
	}

	getAuthInstance() {
		return this.auth;
	}
}
